import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import ModernHeader from '@/components/ModernHeader';
import MobileBottomNav from '@/components/MobileBottomNav';
import CommandPalette from '@/components/CommandPalette';
import { Input } from '@/components/ui/input';
import { X as XIcon, Search as SearchIcon, XCircle as XCircleIcon } from 'lucide-react';
import ManagePage from '@/pages/ManagePage';
import LLMSettings from '@/pages/admin/LLMSettings';
import { Recipe, RecipeFormData } from '@/types/recipe';
import { useRecipeStore } from '@/stores/recipeStore';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './components/ThemeProvider';
import { useAuth } from './contexts/AuthContext';
import { LoginPage } from './pages/LoginPage';
import { ProfileCompletionModal } from './components/ProfileCompletionModal';
import { WelcomeModal } from './components/WelcomeModal';
import { ProfileCreationError } from './components/ProfileCreationError';
import './utils/welcomeModal'; // Import for dev console functions
// import { testGeminiConnection } from '@/services/geminiService.ts'; // Test import commented out
import AssistantWidget from '@/components/Assistant/AssistantWidget'; // Import AssistantWidget
import ImageGenerationModalContainer from '@/components/ImageGenerationModal/ImageGenerationModalContainer';
import RecipeReviewPage from "./pages/RecipeReviewPage";
import ImportIndexPage from '@/pages/import/index';
import PageLayout from '@/components/layout/PageLayout';

// Import route components
import HomePage from '@/pages/HomePage';
import RecipeDetailPage from '@/pages/RecipeDetailPage';
import RecipeNewPage from '@/pages/RecipeNewPage';
import { RecipeCreationLauncher } from '@/components/recipe-creator';
import StudioV2Layout from "./pages/studio/StudioV2Layout";
import { RecipeStudioProvider } from "./pages/studio/RecipeStudioContext";
import Step1CoreQuery from "./pages/studio/Step1CoreQuery";
import Step2PersonaComplexity from "./pages/studio/Step2PersonaComplexity";
import Step3DietarySpecial from "./pages/studio/Step3DietarySpecial";
import Step4Review from "./pages/studio/Step4Review";
import StudioIndex from "./pages/studio/index";

// Main App component that uses the auth context
function App() {
  const location = useLocation();
  const isStudio = location.pathname.startsWith("/studio");
  const isIntro = location.pathname === "/intro";

  const { user, role, needsProfileCompletion, isLoading: authLoading, signOut, refreshProfile, profileDoesNotExist } = useAuth();
  const navigate = useNavigate();
  
  // Debug logging
  useEffect(() => {
    // Removed verbose user logging
  }, [user, role])

  const {
    recipes,
    filters,
    fetchRecipes,
    fetchStudioRecipes,
    addRecipe,
    deleteRecipe,
    setSearchQuery,
    setShowFavoritesOnly,
    getFilteredRecipes,
  } = useRecipeStore();

  const [mobileTab, setMobileTab] = useState('home');
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);

  const CATEGORIES = [
    'Asian',
    'Sri Lankan',
    'Mexican',
    'Latin American',
    'Desserts',
    'Drinks',
    'Sauces',
    'Other',
    'To Try'
  ] as const;

  useEffect(() => {
    if (user && !authLoading) {
      fetchRecipes().catch(() => undefined);
      fetchStudioRecipes().catch(() => undefined);
    }
  }, [user, authLoading, fetchRecipes, fetchStudioRecipes]);

  // Check if welcome modal should be shown for first-time users
  useEffect(() => {
    if (user && !authLoading && !needsProfileCompletion) {
      const welcomeDismissed = localStorage.getItem('chezalex-welcome-dismissed');
      if (!welcomeDismissed) {
        // Add a small delay to ensure the app is fully loaded
        const timer = setTimeout(() => {
          setWelcomeModalOpen(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [user, authLoading, needsProfileCompletion]);

  // Handle mobile tab changes
  const handleMobileTabChange = (tab: string) => {
    setMobileTab(tab);
    
    switch (tab) {
      case 'home':
        setShowFavoritesOnly(false);
        navigate('/');
        break;
      case 'favorites':
        setShowFavoritesOnly(true);
        navigate('/');
        break;
      case 'manage':
        if (role === 'admin' || role === 'reader') {
          navigate('/manage');
        }
        break;
    }
  };

  const filteredRecipes = getFilteredRecipes();

  // Navigation handlers for routed pages
  const handleEditRecipeClick = (recipe: Recipe) => {
    // Try to get dashboard tab from location or default to 'recipes'
    let tab = 'recipes';
    if (location.pathname === '/manage') {
      const params = new URLSearchParams(location.search);
      tab = params.get('tab') || 'recipes';
    }
    navigate(`/recipe/${recipe.id}?edit=true`, { state: { fromManage: true, tab } });
  };

  const handleAddNewRecipeClick = () => {
    navigate('/recipe/new');
  };

  // Delete recipe handler for ManagePage
  const handleDeleteRecipe = async (recipeId: string) => {
    try {
      await deleteRecipe(recipeId);
    } catch {
    }
  };


  // Add recipe handler for ManagePage import functionality
  const handleAddRecipeSubmit = async (formData: RecipeFormData) => {
    try {
      await addRecipe(formData);
    } catch {
    }
  };

  // Prevent scrollbar layout shifts globally
  useEffect(() => {
    // Inject global CSS to prevent layout shifts
    const style = document.createElement('style');
    style.id = 'scrollbar-fix';
    style.textContent = `
      /* Reserve space for scrollbar to prevent layout jumping */
      html {
        overflow-y: scroll;
        scrollbar-gutter: stable;
      }

      /* Tell Radix to skip its own scrollbar-compensation padding.
         We already reserve scrollbar space via scrollbar-gutter: stable
         above, so Radix's padding-right would cause a double layout shift. */
      :root {
        --radix-scroll-lock-compensation: 0px;
      }

      /* Prevent button click propagation issues */
      button {
        -webkit-tap-highlight-color: transparent;
      }

      /* Stabilize dropdown and modal animations */
      [data-radix-popper-content-wrapper] {
        z-index: 50 !important;
      }
    `;
    
    // Only add if not already present
    if (!document.getElementById('scrollbar-fix')) {
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById('scrollbar-fix');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  // Show loading screen during auth check
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FAF7F2] to-[#F5F0E8]">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terracotta-500 mx-auto"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (profileDoesNotExist) {
    return <ProfileCreationError />;
  }
 
  // Show login if not authenticated
  if (!user) {
    return <LoginPage />;
  }

  // User is authenticated, show the main application
  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-[#FAF7F2] to-[#F5F0E8]">
        {/* Fixed Professional Header */}
        {!isIntro && (
          <ModernHeader
            isVisible={true}
            onSearchOpen={() => setMobileSearchOpen(true)}
            onAddRecipe={handleAddNewRecipeClick}
            onManage={() => navigate('/manage')}
            onSignOut={signOut}
            userEmail={user.email}
            userRole={role || undefined}
            onHome={() => navigate('/')}
            isOnManagePage={window.location.pathname === '/manage'}
            searchQuery={filters.searchQuery}
            onSearchChange={setSearchQuery}
            onOpenStudio={() => navigate('/studio')} // Navigate to studio page
            isAdmin={role === 'admin'} // Pass admin status
          />
        )}
        
        {/* Command Palette */}
        <CommandPalette
          open={commandPaletteOpen}
          onOpenChange={setCommandPaletteOpen}
          recipes={recipes}
          onAddRecipe={handleAddNewRecipeClick}
          onManage={() => navigate('/manage')}
          isAdmin={role === 'admin'}
        />
        
        {/* Main Content - Add top padding for fixed header */}
        <main className={isIntro ? "" : "pt-[70px] pb-20 md:pb-8"}>
          <Routes>
            {/* Routes that use the standard page layout */}
            <Route element={<PageLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/recipe/:id" element={<RecipeDetailPage />} />
              <Route path="/recipe/new" element={<RecipeCreationLauncher />} />
              <Route path="/recipe/new/form" element={<RecipeNewPage />} />
              <Route path="/manage" element={
                <ManagePage
                  recipes={recipes}
                  categories={CATEGORIES}
                  onEdit={handleEditRecipeClick}
                  onDelete={handleDeleteRecipe}
                  onImportRecipe={async (formData: any) => {
                    await handleAddRecipeSubmit({ ...formData, isFavorite: false });
                  }}
                  isAdmin={role === 'admin'}
                />
              } />
              <Route path="/manage/llm" element={<LLMSettings />} />
            </Route>

            {/* Routes with custom, full-width layouts */}
            <Route path="/intro" element={<LoginPage />} />
            <Route path="/studio" element={<RecipeStudioProvider><StudioV2Layout /></RecipeStudioProvider>}>
              <Route index element={<StudioIndex />} />
              <Route path="step/1" element={<Step1CoreQuery />} />
              <Route path="step/2" element={<Step2PersonaComplexity />} />
              <Route path="step/3" element={<Step3DietarySpecial />} />
              <Route path="step/4" element={<Step4Review />} />
              <Route path="step/5" element={<RecipeReviewPage />} />
            </Route>
            <Route path="/import" element={<ImportIndexPage />} />
          </Routes>
        </main>
        
        {/* Mobile Bottom Navigation - hidden in studio which has its own action bar */}
        {!isStudio && (
          <MobileBottomNav
            activeTab={mobileTab}
            onTabChange={handleMobileTabChange}
            onAddRecipe={handleAddNewRecipeClick}
            onOpenStudio={() => navigate('/studio')}
            isAdmin={role === 'admin'}
          />
        )}

        {/* Mobile Search Overlay */}
        {mobileSearchOpen && (
          <div className="md:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm animate-in fade-in-0">
            <div className="fixed top-0 left-0 right-0 p-4 bg-background border-b">
              <div className="relative">
                <Input
                  placeholder="Search all recipes..."
                  value={filters.searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-20 h-12 text-lg" // Increased pr for two buttons
                  autoFocus
                />
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                {filters.searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-10 top-1/2 transform -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search query"
                  >
                    <XCircleIcon className="h-5 w-5" />
                  </button>
                )}
                <button
                  onClick={() => setMobileSearchOpen(false)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground" // Adjusted right
                  aria-label="Close search"
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="mt-4 px-2 max-h-[70vh] overflow-y-auto">
                {filteredRecipes.map((recipe) => (
                  <div
                    key={recipe.id}
                    onClick={() => {
                      navigate(`/recipe/${recipe.id}`);
                      setMobileSearchOpen(false);
                    }}
                    className="p-4 mb-2 bg-gray-800/50 hover:bg-gray-700/80 rounded-lg cursor-pointer"
                  >
                    <p className="text-white font-semibold">{recipe.title}</p>
                    <p className="text-gray-400 text-sm line-clamp-1">{recipe.description}</p>
                  </div>
                ))}
                {filters.searchQuery && filteredRecipes.length === 0 && (
                  <div className="p-8 text-center text-gray-400">
                    <p>No recipes found for "{filters.searchQuery}"</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Assistant Widget - Placed here to be on top of other content, visible if user is logged in */}
        {!isStudio && user && <AssistantWidget currentRecipe={null} onOpenStudio={() => navigate('/studio')} />}
        
        {/* Image Generation Modal - PHASE 1: Recipe Detail Page Only */}
        <ImageGenerationModalContainer />
        
        {/* Profile Completion Modal - Show when user needs to complete profile */}
        {user && needsProfileCompletion && (
          <ProfileCompletionModal
            isOpen={needsProfileCompletion}
            onComplete={refreshProfile}
          />
        )}
        
        {/* Welcome Modal - Show for first-time users */}
        <WelcomeModal
          isOpen={welcomeModalOpen}
          onClose={() => setWelcomeModalOpen(false)}
        />

      </div>
    </>
  );
}

// Provider wrapper component
export default function AppWrapper() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ThemeProvider>
  );
}
